/**
 * Account-surface visibility for trading verticals that have been switched off.
 *
 * Disabling a vertical removes its *entry points* (nav, picker, routes). Rows
 * the user already holds must still be reachable so they can close. These
 * helpers are the other half of that contract: empty Positions / margin /
 * liquidation chrome stays hidden for users who never traded that vertical.
 */
import { InstrumentTypeResponse } from "../../codegen-api";
import { isInstrumentTypeEnabled } from "./tradingTypes";

export type AccountSurfacePosition = {
  instrument_type?: string;
  amount?: string | number | null;
};

export type AccountSurfaceFlags = {
  /** Positions tab, position history, margin health, liquidation chrome. */
  showDerivativeUi: boolean;
  /** Funding history and other perpetual-only account surfaces. */
  showPerpUi: boolean;
};

const isOpenPosition = (position: AccountSurfacePosition): boolean =>
  Number(position.amount) !== 0;

export const hasOpenPositionsOfType = (
  positions: AccountSurfacePosition[] | undefined | null,
  instrumentType: InstrumentTypeResponse
): boolean => {
  if (!positions?.length) {
    return false;
  }
  return positions.some(
    (position) =>
      position.instrument_type === instrumentType && isOpenPosition(position)
  );
};

/**
 * A vertical's account chrome is visible while it is enabled, or while the
 * user still holds an open position in it after it was switched off.
 */
export const shouldShowInstrumentAccountUi = (
  instrumentType: InstrumentTypeResponse,
  positions?: AccountSurfacePosition[] | null
): boolean =>
  isInstrumentTypeEnabled(instrumentType) ||
  hasOpenPositionsOfType(positions, instrumentType);

/** Perps or options — anything with positions, margin and liquidation. */
export const shouldShowDerivativeAccountUi = (
  positions?: AccountSurfacePosition[] | null
): boolean =>
  shouldShowInstrumentAccountUi(InstrumentTypeResponse.Perpetual, positions) ||
  shouldShowInstrumentAccountUi(InstrumentTypeResponse.Option, positions);

export const getAccountSurfaceFlags = (
  positions?: AccountSurfacePosition[] | null
): AccountSurfaceFlags => ({
  showDerivativeUi: shouldShowDerivativeAccountUi(positions),
  showPerpUi: shouldShowInstrumentAccountUi(
    InstrumentTypeResponse.Perpetual,
    positions
  ),
});
